import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AnimatedSection } from '../components/AnimatedSection';
import { CheckCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const projectTypes = ['UI/UX Design', 'Product Design', 'Design System', 'Web & App Design', 'Brand Identity', 'Other'];

const FloatInput = ({ id, label, value, onChange, type = 'text', required, as: Tag = 'input', rows }) => (
  <div className="float-group">
    {Tag === 'textarea' ? (
      <textarea id={id} value={value} onChange={onChange} placeholder=" " required={required} rows={rows || 5}
        className="float-input" style={{ resize: 'none' }} data-testid={`contact-input-${id}`} />
    ) : (
      <input id={id} type={type} value={value} onChange={onChange} placeholder=" " required={required}
        className="float-input" data-testid={`contact-input-${id}`} />
    )}
    <label htmlFor={id} className="float-label">{label}</label>
  </div>
);

export default function Contact() {
  const [form, setForm] = useState({ name:'', email:'', company:'', message:'', project_type:'' });
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');

  const upd = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus('loading');
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrMsg(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    }
  };

  const reset = () => { setForm({ name:'', email:'', company:'', message:'', project_type:'' }); setStatus('idle'); setErrMsg(''); };

  return (
    <div data-testid="contact-page" style={{ paddingTop: 64, background: 'var(--canvas)' }}>
      {/* Hero */}
      <section style={{ borderBottom: '1px solid var(--border)', paddingTop: 80, paddingBottom: 80 }}>
        <div className="container-site">
          <AnimatedSection>
            <span className="label-sm">Get In Touch</span>
            <h1 className="heading-xl" style={{ fontSize: 'clamp(48px, 9vw, 100px)', marginTop: 20 }}>
              Let&rsquo;s Build<br /><span style={{ color: 'var(--accent)' }}>Together</span>
            </h1>
            <p className="body-base" style={{ maxWidth: 440, marginTop: 24, fontSize: 16 }}>
              Tell us about your project. We respond to all inquiries within 24 hours.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Form + Info */}
      <section style={{ paddingBottom: 96 }}>
        <div className="container-site">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 80, paddingTop: 64 }}>
            {/* Form */}
            <div>
              <AnimatePresence mode="wait">
                {status === 'success' ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                    data-testid="contact-success-state"
                    style={{ paddingTop: 40 }}
                  >
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.15, type: 'spring', stiffness: 220 }}>
                      <CheckCircle size={44} style={{ color: 'var(--success)' }} />
                    </motion.div>
                    <h2 className="heading-lg" style={{ fontSize: 32, margin: '20px 0 12px' }}>Message Received.</h2>
                    <p className="body-base" style={{ maxWidth: 380, marginBottom: 24 }}>
                      Thank you for reaching out. We'll respond within 24 hours.
                    </p>
                    <motion.div style={{ height: 1, background: 'var(--ink-950)', width: 0 }} animate={{ width: 80 }} transition={{ delay: 0.3, duration: 0.7 }} />
                    <button onClick={reset} data-testid="contact-send-another-btn"
                      style={{ marginTop: 24, fontFamily: 'Inter', fontSize: 12, color: 'var(--ink-500)', background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '0.05em' }}>
                      Send another message →
                    </button>
                  </motion.div>
                ) : (
                  <motion.form key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} onSubmit={submit} data-testid="contact-form">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
                      <FloatInput id="name"    label="Full Name"     value={form.name}    onChange={upd('name')}    required />
                      <FloatInput id="email"   label="Email"         value={form.email}   onChange={upd('email')}   type="email" required />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
                      <FloatInput id="company" label="Company (opt)" value={form.company} onChange={upd('company')} />
                      {/* Project type */}
                      <div className="float-group">
                        <select id="project_type" value={form.project_type} onChange={upd('project_type')}
                          className="float-input" style={{ appearance: 'none', color: form.project_type ? 'var(--ink-950)' : 'transparent' }}
                          data-testid="contact-input-project_type">
                          <option value=""> </option>
                          {projectTypes.map(t => <option key={t} value={t} style={{ color: 'var(--ink-950)', background: 'var(--canvas)' }}>{t}</option>)}
                        </select>
                        <label htmlFor="project_type" className="float-label"
                          style={form.project_type ? { top: 0, fontSize: 10, color: 'var(--accent)', fontFamily: 'JetBrains Mono', letterSpacing: '0.08em', textTransform: 'uppercase' } : {}}>
                          Project Type
                        </label>
                      </div>
                    </div>
                    <FloatInput id="message" label="Tell us about your project" value={form.message} onChange={upd('message')} required as="textarea" rows={5} />

                    {status === 'error' && (
                      <motion.p initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                        style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--error)', marginBottom: 16 }}
                        data-testid="contact-error-msg">
                        {errMsg}
                      </motion.p>
                    )}

                    <button type="submit" className="btn-primary" data-testid="contact-submit-btn"
                      style={{ marginTop: 8, opacity: status === 'loading' ? 0.7 : 1, pointerEvents: status === 'loading' ? 'none' : 'auto' }}>
                      {status === 'loading' ? (
                        <>
                          <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                            style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%' }} />
                          Sending...
                        </>
                      ) : <>Send Message <ArrowRight size={14} /></>}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>

            {/* Info */}
            <AnimatedSection delay={0.2}>
              <div style={{ paddingTop: 40 }}>
                <span className="label-sm" style={{ display: 'block', marginBottom: 16 }}>Direct Contact</span>
                <a href="mailto:Bhardwajh.1603@gmail.com"
                  style={{ fontFamily: 'Space Grotesk', fontSize: 15, fontWeight: 600, color: 'var(--ink-950)', textDecoration: 'none', letterSpacing: '-0.01em', display: 'block', marginBottom: 4 }}
                  onMouseEnter={e => e.target.style.color = 'var(--accent)'}
                  onMouseLeave={e => e.target.style.color = 'var(--ink-950)'}
                  data-testid="contact-email-link"
                >
                  Bhardwajh.1603@gmail.com
                </a>
                <a href="mailto:ganeshjena2208@gmail.com"
                  style={{ fontFamily: 'Space Grotesk', fontSize: 15, fontWeight: 600, color: 'var(--ink-950)', textDecoration: 'none', letterSpacing: '-0.01em', display: 'block', marginBottom: 6 }}
                  onMouseEnter={e => e.target.style.color = 'var(--accent)'}
                  onMouseLeave={e => e.target.style.color = 'var(--ink-950)'}
                >
                  ganeshjena2208@gmail.com
                </a>
                <div style={{ display: 'flex', gap: 24, marginTop: 4, marginBottom: 40 }}>
                  <span style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: 'var(--ink-300)' }}>+91 81306 69687</span>
                  <span style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: 'var(--ink-300)' }}>+91 70244 38039</span>
                </div>

                <div style={{ height: 1, background: 'var(--border)', marginBottom: 40 }} />

                <span className="label-sm" style={{ display: 'block', marginBottom: 20 }}>What to Expect</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {[
                    ['24h Response',      'We acknowledge every inquiry within one business day.'],
                    ['Honest Scoping',    'We\'ll tell you what\'s realistic, not what you want to hear.'],
                    ['Free Discovery',    'A 30-min strategy call before any commitment.'],
                  ].map(([title, desc], i) => (
                    <motion.div key={i} initial={{ opacity: 0, x: 16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                      style={{ display: 'flex', gap: 14 }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--ink-950)', marginTop: 6, flexShrink: 0 }} />
                      <div>
                        <p style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 600, color: 'var(--ink-950)', marginBottom: 2 }}>{title}</p>
                        <p style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--ink-500)' }}>{desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Quote card */}
                <div style={{ marginTop: 48, padding: 28, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
                  <p style={{ fontFamily: 'Space Grotesk', fontSize: 15, fontWeight: 500, color: 'var(--ink-950)', lineHeight: 1.6, letterSpacing: '-0.01em', marginBottom: 16 }}>
                    &ldquo;We don&rsquo;t just create visuals. We shape how people feel about your brand.&rdquo;
                  </p>
                  <span className="label-sm" style={{ color: 'var(--accent)', marginBottom: 0 }}>Ganesh & Hemant, Founders</span>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
}
